import { useCallback, useContext, useState } from 'react';

import { ListTable, Progress } from '@atom/mui';

import FilesTabContext from './FilesTabContext';
import { canCreateSubfolder, sortMedia } from './filesTabUtilities';
import MediaTableRow from './MediaTableRow';
import { MediaRow, MediaSortColumn, SortDirection } from './types';

import classes from './filesTab.module.css';

const { TableHead, TableBody, TableRow, TableCell } = ListTable;

const TABLE_OFFSET_TOP =
  'calc(var(--detailFixedHeaderOffset) + var(--filesTabHeaderHeight))';

const COLUMNS: {
  field: MediaSortColumn | '';
  label: string;
  sortable: boolean;
  width?: string;
  align?: 'left' | 'right';
  dataCy?: string;
}[] = [
  { field: '', label: '', sortable: false, width: '2rem' },
  { field: 'name', label: 'Name', sortable: true, dataCy: 'filesTabSortName' },
  {
    field: 'fileExtension',
    label: 'File Type',
    sortable: true,
    dataCy: 'filesTabSortFileType',
  },
  {
    field: 'createdBy',
    label: 'Created By',
    sortable: true,
    dataCy: 'filesTabSortCreatedBy',
  },
  {
    field: 'createdDate',
    label: 'Created On',
    sortable: true,
    dataCy: 'filesTabSortCreatedOn',
  },
  { field: '', label: '', sortable: false, width: '3rem', align: 'right' },
];

const MediaTable = () => {
  const {
    canCreate,
    canUpdate,
    canDelete,
    selectedItems,
    selectedMediaType,
    toggleSelectItem,
    selectFolder,
    folders,
    media,
    foldersLoading,
    mediaLoading,
  } = useContext(FilesTabContext);

  const [sortBy, setSortBy] = useState<MediaSortColumn | null>('createdDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const getSortDirection = useCallback(
    (field: MediaSortColumn): SortDirection | false => {
      if (sortBy !== field) {
        return false;
      }

      return sortDirection;
    },
    [sortBy, sortDirection],
  );

  const handleSortDirectionChange =
    (field: MediaSortColumn) => (direction: SortDirection) => {
      setSortBy(field);
      setSortDirection(direction);
    };

  const sortedMedia = sortMedia(sortBy, sortDirection === 'asc', media);
  const sortedFolders = sortMedia(sortBy, sortDirection === 'asc', folders);
  const rows: MediaRow[] = selectedMediaType
    ? sortedMedia
    : [...sortedFolders, ...sortedMedia];

  const loading = mediaLoading || foldersLoading;
  const showInitialLoading = loading && rows.length === 0;

  return (
    <div className={classes.tableContainer}>
      <ListTable
        offsetTop={TABLE_OFFSET_TOP}
        TableContainerProps={{ style: { overflow: 'auto' } }}
      >
        <TableHead>
          <TableRow header>
            {COLUMNS.map((col, index) => (
              <TableCell
                key={col.field || col.label || `spacer-${index}`}
                variant="head"
                width={col.width}
                align={col.align}
                {...(col.dataCy ? { 'data-cy': col.dataCy } : {})}
                {...(col.sortable && col.field
                  ? {
                      sortDirection: getSortDirection(col.field),
                      onSortChange: handleSortDirectionChange(col.field),
                    }
                  : {})}
              >
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => {
            const isFolder = !row.fileId;
            const depth = (row.ancestors?.length ?? 0) + 1;
            const showFolderCreate = canCreate && canCreateSubfolder(depth);

            return (
              <MediaTableRow
                key={row.id}
                row={row}
                isFolder={isFolder}
                checked={selectedItems.has(row.id)}
                showFolderCreate={showFolderCreate}
                canUpdate={canUpdate}
                canDelete={canDelete}
                onToggleCheck={toggleSelectItem}
                onOpenFolder={selectFolder}
              />
            );
          })}
        </TableBody>
      </ListTable>
      {showInitialLoading && <Progress style={{ height: '100%' }} />}
    </div>
  );
};

export default MediaTable;
